import React, { useState, useEffect } from 'react';
import TherapeuticAreaInitializer from './components/TherapeuticAreaInitializer';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Layout, Button, Space, Typography, Spin, Tag, Modal, Form, Input, App as AntApp, ConfigProvider, theme } from 'antd';
import { 
  LogoutOutlined,
  MoonOutlined,
  SunOutlined
} from '@ant-design/icons';
import './App.css';
import './responsive.css';

// Import components
import LandingPage from './components/LandingPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import ContactPage from './components/ContactPage';
import SubscribePage from './components/SubscribePage';
import SubscriptionPending from './components/SubscriptionPending';
import PlatformPage from './components/PlatformPage';
import DataCoveragePage from './components/DataCoveragePage';
import DermatologyMarketPage from './components/DermatologyMarketPage';
import PsoriasisMarketPage from './components/PsoriasisMarketPage';
import SkinCancerMarketPage from './components/SkinCancerMarketPage';
import MarketsPage from './components/MarketsPage';
import PulsePage from './components/PulsePage';
import PulseArticlePage from './components/PulseArticlePage';
import PressPage from './components/PressPage';
import PressArticlePage from './components/PressArticlePage';
import AboutPage from './components/AboutPage';
import NotFoundPage from './components/NotFoundPage';
import ExecutiveSummary from './components/ExecutiveSummary';
import MarketOverview from './components/MarketOverview';
import PipelineAnalysis from './components/PipelineAnalysis';
import RegionalAnalysis from './components/RegionalAnalysis';
import CountryInsights from './components/CountryInsights';
import CompanyPerformance from './components/CompanyPerformance';
import FilterPanel from './components/FilterPanel';
import LoginScreen from './components/LoginScreen';
import AdminPortal from './components/AdminPortal';
import OutreachPortal from './components/OutreachPortal';
import Seo from './components/Seo';
import ScrollToTop from './components/ScrollToTop';
import { FilterProvider, useFilters } from './contexts/FilterContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import IdleTimeoutManager from './components/IdleTimeoutManager';
import API_CONFIG from './config';


const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;



// Dashboard Content Component (uses filters)
const DashboardContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('portal_active_tab') || 'executive-summary';
  });

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    sessionStorage.setItem('portal_active_tab', key);
  };
  
  // const [loading, setLoading] = useState(false); // Reserved for future use
  const { user, logout } = useAuth();
  const { filters } = useFilters();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  // Pre-warm the backend service when dashboard loads (helps with cold starts)
  useEffect(() => {
    const prewarmBackend = async () => {
      try {
        // Fire-and-forget health check to wake up the backend
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        await fetch(`${API_CONFIG.BASE_URL}/health/detailed`, {
          method: 'GET',
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        console.log('Backend pre-warmed successfully');
      } catch (error) {
        // Silently fail - this is just pre-warming
        console.debug('Backend pre-warm failed (non-critical):', error);
      }
    };

    // Only pre-warm if user is authenticated
    if (user) {
      prewarmBackend();
    }
  }, [user]);

  // Generate dynamic title based on therapeutic area and user access
  const getHeaderTitle = () => {
    if (!user) {
      return 'Pharmaceutical Market Intelligence';
    }

    const currentTA = filters.therapeuticArea;
    
    // Handle trial version
    if (user.role === 'trial') {
      return 'Skin Cancer Drugs Market - Trial Version';
    }
    
    // Handle specific therapeutic areas
    switch (currentTA) {
      // Dermatology domain
      case 'Dermatology':
        return 'Dermatology Drugs Market - In Sight';
      case 'Psoriasis':
        return 'Psoriasis Drugs Market - Focus';
      case 'Skin Cancer':
        return 'Skin Cancer Drugs Market - Focus';
      case 'Skin Cancer - Trial Version':
        return 'Skin Cancer Drugs Market - Trial Version';
      // Ophthalmology domain
      case 'Ophthalmology':
        return 'Ophthalmic Drugs Market - In Sight';
      case 'Retinal Disorders':
        return 'Retinal Disorder Drugs Market - Focus';
      case 'Glaucoma':
        return 'Glaucoma Drugs Market - Focus';
      // Cell, Gene & RNA domain
      case 'Cell, Gene & RNA Therapies':
        return 'Cell, Gene & RNA Therapies Market - In Sight';
      case 'All':
        return user.licensed_domains?.includes('Dermatology') ? 
          'Dermatology Drugs Market - In Sight' : 
          user.licensed_domains?.includes('Ophthalmology') ?
          'Ophthalmic Drugs Market - In Sight' :
          user.licensed_domains?.includes('Cell, Gene & RNA') ?
          'Cell, Gene & RNA Therapies Market - In Sight' :
          'Pharmaceutical Market Intelligence';
      default:
        return currentTA ? `${currentTA} Drugs Market - Focus` : 'Pharmaceutical Market Intelligence';
    }
  };

  const renderContent = () => {
    const loading = false; // Static value for now
    switch (activeTab) {
      case 'executive-summary':
        return <ExecutiveSummary data={{}} loading={loading} />;
      case 'market-overview':
        return <MarketOverview data={{}} loading={loading} activeTab="market-overview" />;
      case 'market-breakdowns':
        return <MarketOverview data={{}} loading={loading} activeTab="market-breakdowns" />;
      case 'regional-analysis':
        return <RegionalAnalysis data={{}} loading={loading} />;
      case 'country-insights':
        return <CountryInsights data={{}} loading={loading} />;
      case 'drug-pipeline':
        return <PipelineAnalysis data={{}} loading={loading} />;
      case 'company-performance':
        return <CompanyPerformance data={{}} loading={loading} />;
      default:
        return <ExecutiveSummary data={{}} loading={loading} />;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Left Sidebar */}
      <Sider width={280} theme="light" style={{ position: 'fixed', height: '100vh', left: 0, top: 0 }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100vh',
          overflow: 'hidden'
        }}>
          {/* Logo Section - Fixed */}
          <div style={{ 
            padding: '4px 2px', 
            borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
              <img 
                src={process.env.PUBLIC_URL + '/sns-focus-logo.png'} 
                alt="SNS Intelligence" 
                style={{ height: 220, width: 'auto', objectFit: 'contain' }}
              />
            </div>
          </div>

          {/* Filters & Slicers - Scrollable */}
          <div 
            className="sidebar-scrollable"
            style={{ 
              flex: 1, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              paddingRight: '8px' // Add some padding for scrollbar
            }}
          >
            <FilterPanel activeTab={activeTab} />
          </div>
        </div>
      </Sider>

      {/* Main Content */}
      <Layout style={{ marginLeft: 280 }}>
        {/* Header */}
        <Header style={{ 
          background: isDark ? '#141414' : '#fff', 
          padding: '0 24px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.06)',
          borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`
        }}>
          <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
            {getHeaderTitle()}
          </Title>
          
          <Space size="middle">
            {user?.role === 'admin' && (
              <Tag color="red" style={{ marginRight: 8 }}>
                Admin
              </Tag>
            )}
            <span style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#8c8c8c' }}>
              Welcome, {user?.email || 'admin'}
            </span>
            <Button
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? 'Light' : 'Dark'}
            </Button>
            {user?.role === 'admin' && (
              <Button 
                type="primary"
                onClick={() => navigate('/admin')}
              >
                Admin Center
              </Button>
            )}
            <Button 
              icon={<LogoutOutlined />} 
              onClick={logout}
              danger
            >
              Logout
            </Button>
          </Space>
        </Header>

        {/* Content */}
        <Content style={{ padding: '24px', background: isDark ? '#000000' : '#f5f7fa' }}>
          {/* Horizontal Tab Navigation */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              width: '100%',
              justifyContent: 'space-between'
            }}>
              {[
                { key: 'executive-summary', label: 'Executive Summary' },
                { key: 'market-overview', label: 'Market Overview' },
                { key: 'market-breakdowns', label: 'Market Breakdowns' },
                { key: 'regional-analysis', label: 'Regional Analysis' },
                { key: 'country-insights', label: 'Country Insights' },
                { key: 'drug-pipeline', label: 'Drug Pipeline' },
                { key: 'company-performance', label: 'Company Performance' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    flex: 1,
                    margin: '0 4px',
                    padding: '12px 16px',
                    border: activeTab === tab.key ? 'none' : `1px solid ${isDark ? '#434343' : '#e8e8e8'}`,
                    background: activeTab === tab.key ? '#1890ff' : (isDark ? '#141414' : '#fff'),
                    color: activeTab === tab.key ? '#fff' : (isDark ? 'rgba(255, 255, 255, 0.85)' : '#333'),
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '550',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: activeTab === tab.key ? '0 2px 8px rgba(24, 144, 255, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.background = isDark ? '#1f1f1f' : '#f8f9fa';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.key) {
                      e.currentTarget.style.background = isDark ? '#141414' : '#fff';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
};

// Main Dashboard Component (wraps content with FilterProvider and ThemeProvider)
const Dashboard: React.FC = () => {
  const { isDark } = useTheme();
  
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorBgContainer: isDark ? '#141414' : '#ffffff',
          colorBgElevated: isDark ? '#1f1f1f' : '#ffffff',
          colorBgLayout: isDark ? '#000000' : '#f5f7fa',
          colorBorder: isDark ? '#434343' : '#d9d9d9',
          colorText: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
          colorTextSecondary: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
        },
      }}
    >
      <FilterProvider>
        <TherapeuticAreaInitializer>
          <DashboardContent />
        </TherapeuticAreaInitializer>
      </FilterProvider>
    </ConfigProvider>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin Protected Route Component
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
};

// Login Route Component
const LoginRoute: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    // Redirect admins to admin center, others to dashboard
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/app" replace />;
  }

  return <LoginScreen />;
};

// Main App Component with Routing
const AppRoutes: React.FC = () => {
  const { user, changePassword } = useAuth();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [forceModalForm] = Form.useForm();

  useEffect(() => {
    setIsModalVisible(!!user?.password_reset_required);
  }, [user?.password_reset_required]);

  const handlePasswordUpdate = async (values: any) => {
    if (values.newPassword !== values.confirmPassword) {
      forceModalForm.setFields([
        {
          name: 'confirmPassword',
          errors: ['Passwords do not match'],
        },
      ]);
      return;
    }

    // When password_reset_required is true, we don't need to send currentPassword
    // since the user just authenticated with the temporary password
    const result = await changePassword(null as any, values.newPassword);
    if (result.success) {
      forceModalForm.resetFields();
      setIsModalVisible(false);
    } else {
      forceModalForm.setFields([
        {
          name: 'newPassword',
          errors: [result.error || 'Unable to change password'],
        },
      ]);
    }
  };

  return (
    <>
      <Modal
        title="Password Change Required"
        open={isModalVisible}
        closable={false}
        maskClosable={false}
        footer={null}
      >
        <Text type="danger">
          You must change your temporary password before continuing.
        </Text>
        <Form
          form={forceModalForm}
          layout="vertical"
          onFinish={handlePasswordUpdate}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="newPassword"
            label="New password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            rules={[{ required: true, message: 'Please confirm the new password' }]}
          >
            <Input.Password placeholder="Confirm password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Router>
        <ScrollToTop />
        <Seo />
        <Routes>
          {/* LAUNCH: Root route shows LandingPage */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/waitlist" element={<Navigate to="/" replace />} />
          <Route path="/launch-preview" element={<Navigate to="/" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/terms-of-service" element={<TermsPage />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/data-coverage" element={<DataCoveragePage />} />
          <Route path="/markets/dermatology" element={<DermatologyMarketPage />} />
          <Route path="/markets/psoriasis" element={<PsoriasisMarketPage />} />
          <Route path="/markets/skin-cancer" element={<SkinCancerMarketPage />} />
          <Route path="/markets" element={<MarketsPage />} />
          <Route path="/pulse" element={<PulsePage />} />
          <Route path="/pulse/:slug" element={<PulseArticlePage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/press/:slug" element={<PressArticlePage />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/subscription-pending" element={<SubscriptionPending />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/admin" element={
            <AdminProtectedRoute>
              <AdminPortal />
            </AdminProtectedRoute>
          } />
          <Route path="/outreach" element={
            <AdminProtectedRoute>
              <OutreachPortal />
            </AdminProtectedRoute>
          } />
          <Route path="/app/*" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </>
  );
};

// Main App Component with Routing
const App: React.FC = () => {
  return (
    <AntApp>
      <ThemeProvider>
        <AuthProvider>
          <IdleTimeoutManager>
            <AppRoutes />
          </IdleTimeoutManager>
        </AuthProvider>
      </ThemeProvider>
    </AntApp>
  );
};

export default App;